import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { PII_MASK_CLASS } from "@/lib/analytics";
import { useProfile } from "@/queries/customer/customer";
import { createRoute } from "@tanstack/react-router";
import { Building2, MapPin, Receipt, User } from "lucide-react";
import { accountRoute } from "./account-page";

export const accountProfileRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "/",
  component: AccountProfilePage
});

/**
 * Profile page component with tabbed interface
 *
 * Features:
 * - Tabbed layout for User Info, Business Info, Shipping/Billing Addresses
 * - Semantic HTML structure
 * - Accessible tabs with keyboard navigation
 * - Screen reader friendly
 * - Responsive layout
 */
function AccountProfilePage() {
  const { data: profile, status, error } = useProfile();
  useReportLoadFailure(error, "profile page");

  if (status === "pending") {
    return <ProfilePageSkeleton />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400"
          role="alert"
        >
          <p>We couldn&apos;t load your profile. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header inside card */}
      <div className="border-border border-b p-6">
        <h2
          className="text-xl font-semibold text-gray-900 dark:text-white"
          id="profile-heading"
        >
          Profile
        </h2>
        <p
          className="mt-1 text-sm text-gray-600 dark:text-gray-400"
          id="profile-description"
        >
          Manage your business information and addresses
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="user-info" className="w-full">
        <div className="border-border border-b">
          <TabsList className="h-auto w-full justify-start gap-0 rounded-none bg-transparent p-0">
            <TabsTrigger
              value="user-info"
              className="flex items-center gap-2 rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-600 data-[state=active]:shadow-none"
            >
              <User className="h-4 w-4" aria-hidden="true" />
              User Info
            </TabsTrigger>
            <TabsTrigger
              value="business-info"
              className="flex items-center gap-2 rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-600 data-[state=active]:shadow-none"
            >
              <Building2 className="h-4 w-4" aria-hidden="true" />
              Business Info
            </TabsTrigger>
            <TabsTrigger
              value="shipping-addresses"
              className="flex items-center gap-2 rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-600 data-[state=active]:shadow-none"
            >
              <MapPin className="h-4 w-4" aria-hidden="true" />
              Shipping Addresses
            </TabsTrigger>
            <TabsTrigger
              value="billing-address"
              className="flex items-center gap-2 rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-600 data-[state=active]:shadow-none"
            >
              <Receipt className="h-4 w-4" aria-hidden="true" />
              Billing Address
            </TabsTrigger>
          </TabsList>
        </div>

        {/* User Info Tab */}
        <TabsContent value="user-info" className="p-6">
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900">
                <User
                  className="h-8 w-8 text-purple-600 dark:text-purple-400"
                  aria-hidden="true"
                />
              </div>
              {/* Name and email - the most identifying strings in the app, and
                  masked out of session replays (ADR-0006). */}
              <div className={PII_MASK_CLASS}>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {profile?.customer.first_name || ""}{" "}
                  {profile?.customer.last_name || ""}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {profile?.customer.email}
                </p>
              </div>
            </div>

            <div
              className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-gray-700"
              role="status"
              aria-live="polite"
            >
              <p className="text-gray-500 dark:text-gray-400">
                Profile editing coming soon...
              </p>
            </div>
          </div>
        </TabsContent>

        {/* Business Info Tab */}
        <TabsContent value="business-info" className="p-6">
          <div className="space-y-4">
            {profile?.customer.company_name && (
              <div className={PII_MASK_CLASS}>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Company Name
                </p>
                <p className="text-gray-900 dark:text-white">
                  {profile.customer.company_name}
                </p>
              </div>
            )}
            <div
              className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-gray-700"
              role="status"
              aria-live="polite"
            >
              <p className="text-gray-500 dark:text-gray-400">
                Business information editing coming soon...
              </p>
            </div>
          </div>
        </TabsContent>

        {/* Shipping Addresses Tab */}
        <TabsContent value="shipping-addresses" className="p-6">
          <div
            className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-gray-700"
            role="status"
            aria-live="polite"
          >
            <p className="text-gray-500 dark:text-gray-400">
              Shipping addresses will be managed here...
            </p>
          </div>
        </TabsContent>

        {/* Billing Address Tab */}
        <TabsContent value="billing-address" className="p-6">
          <div
            className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-gray-700"
            role="status"
            aria-live="polite"
          >
            <p className="text-gray-500 dark:text-gray-400">
              Billing address will be managed here...
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Skeleton loader for profile page
 */
function ProfilePageSkeleton() {
  return (
    <div>
      <div className="border-border border-b p-6">
        <Skeleton className="h-7 w-24" />
        <Skeleton className="mt-2 h-4 w-64" />
      </div>
      <div className="border-border border-b">
        <div className="flex gap-4 p-1">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-28" />
          <Skeleton className="h-10 w-36" />
          <Skeleton className="h-10 w-32" />
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
      </div>
    </div>
  );
}
